import { create } from 'zustand';

/**
 * Host-app identifier reported by `require('uxp').host.name`. Empirically
 * verified on 2026-05-27: Premiere Pro 2026 (26.0.2) returns `premierepro`.
 * Premiere Pro Beta's literal value is unverified — the resolver below
 * falls back to a substring match on 'beta' (case-insensitive), which
 * tolerates plausible variants like `premiereProBeta` or `premiereprobeta`.
 */
const BETA_NAME_FRAGMENT = 'beta';

/**
 * URL schemes registered by Premiere Pro with macOS Launch Services:
 *
 * - `adobe+ppro://` is claimed *only* by the production Premiere Pro app
 *   (see `Adobe Premiere Pro 2026.app/Contents/Info.plist > CFBundleURLTypes`).
 * - `adobe+pprobeta://` is claimed *only* by Premiere Pro Beta.
 * - `adobeppro://` is claimed by *both*, so it resolves ambiguously to
 *   whichever app the OS chose last and is unsafe to deep-link with — opening
 *   it from a browser may focus the wrong app.
 *
 * The variant-specific schemes above are the only safe targets. Verified via
 * `lsregister -dump` on 2026-05-27.
 */
const SCHEME_PRODUCTION = 'adobe+ppro';
const SCHEME_BETA = 'adobe+pprobeta';

export type PremiereHostInfo = {
  name: string;
  version: string;
};

type PremiereHostState = {
  host: PremiereHostInfo | null;
  setHost: (host: PremiereHostInfo) => void;
};

export const usePremiereHostStore = create<PremiereHostState>((set) => ({
  host: null,
  setHost: (host) => set({ host }),
}));

/**
 * Returns the variant-specific URL scheme (no trailing `://`) for the given
 * host info. Defaults to the production scheme when host info is unavailable
 * (older host build that pre-dates the Ready-event extension) — picking prod
 * is the safer fallback since the vast majority of installs are not beta.
 */
export const getPremiereDeepLinkScheme = (host: PremiereHostInfo | null): string => {
  if (host?.name?.toLowerCase().includes(BETA_NAME_FRAGMENT)) {
    return SCHEME_BETA;
  }

  return SCHEME_PRODUCTION;
};
